import React, {useState, useEffect} from 'react';
import {useHistory} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import styled, {css} from 'styled-components';
import {useTranslation} from 'react-i18next';
import Lottie from 'react-lottie';

import {
    Flex,
    colors,
    mobileMediaQuery,
    notWebMediaQuery,
    PageBodyWrapper,
    tabletMediaQuery,
} from '../styles';
import {selectAvatar} from '../store/actions/user';
import {debugCNitoUrl, preloadImage, useIsCartoonito} from '../helpers';

import Button from '../components/Button';
import CNPlusAvatars from '../assets/avatars/cnplus';
import CNitoAvatars from '../assets/avatars/cnito';
import starBackgroundMobile from '../assets/star_background_mobile.png';
import starBackgroundTablet from '../assets/star_background_tablet.png';

const Onboarding = props => {
    const {t} = useTranslation();
    const history = useHistory();
    const dispatch = useDispatch();
    const userAvatar = useSelector(state => state.user.avatar);
    const isCartoonito = useIsCartoonito();

    const [currentAvatar, setCurrentAvatar] = useState(userAvatar || null);

    useEffect(() => {
        if (
            currentAvatar > (isCartoonito ? CNitoAvatars : CNPlusAvatars).length
        ) {
            setCurrentAvatar(0);
        }
        // eslint-disable-next-line
    }, []);

    const handleDone = () => {
        dispatch(selectAvatar(currentAvatar));

        history.push(`/${debugCNitoUrl()}`);
    };

    const handleSkip = () => {
        dispatch(selectAvatar(null));

        history.push(`/${debugCNitoUrl()}`);
    };

    return (
        <Container cnito={isCartoonito}>
            <Title cnito={isCartoonito}>{t('onboarding.title')}</Title>

            <AvatarGrid margin="40px auto 80px auto">
                {(isCartoonito ? CNitoAvatars : CNPlusAvatars).map(
                    (item, index) => (
                        <AvatarButton
                            key={index}
                            active={currentAvatar === index}
                            inactive={
                                currentAvatar !== null &&
                                currentAvatar !== index
                            }
                            background={isCartoonito ? null : item}
                            cnito={isCartoonito}
                            onClick={() => setCurrentAvatar(index)}
                        >
                            {isCartoonito && (
                                <Lottie
                                    isClickToPauseDisabled
                                    options={{
                                        loop: false,
                                        autoplay: true,
                                        animationData:
                                            currentAvatar === index
                                                ? item[1]
                                                : item[0],
                                        isClickToPauseDisabled: true,
                                        isStopped: false,
                                        isPaused: false,
                                    }}
                                />
                            )}
                        </AvatarButton>
                    ),
                )}
            </AvatarGrid>

            <Button
                width="259px"
                margin="0"
                onClick={currentAvatar === null ? handleSkip : handleDone}
                cnito={isCartoonito}
            >
                {t(`onboarding.${currentAvatar === null ? 'skip' : 'done'}`)}
            </Button>
        </Container>
    );
};

const Container = styled(PageBodyWrapper)`
    margin-left: 0;
    margin-right: 0;
    max-width: 100%;
    position: relative;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: calc(100vh - 392px);
    width: 100%;
    box-sizing: border-box;
    background-color: ${props => (props.cnito ? '#32bf5e' : colors.lightBlue)};
    border-top: 20px ​solid #000;

    margin-top: 108px;
    padding-top: 32px;
    padding-bottom: 40px;

    ${notWebMediaQuery} {
        margin-top: 0px;
        padding-top: 220px;
        padding-bottom: 80px;
    }

    ${mobileMediaQuery} {
        padding-top: 152px;
    }

    ${props =>
        props.cnito &&
        css`
            border-top-color: #32bf5e;

            background-image: url(${props => starBackgroundTablet});
            background-size: 500px;
            background-position: center;
            background-repeat: repeat;

            ${mobileMediaQuery} {
                background-image: url(${props => starBackgroundMobile});
                background-size: contain;
                background-position: center;
                background-repeat: repeat;
            }
        `};
`;

const AvatarGrid = styled(Flex)`
    ${tabletMediaQuery} {
        max-width: 420px;
    }

    ${mobileMediaQuery} {
        max-width: 320px;
    }
`;

const Title = styled.h1`
    color: rgb(0, 0, 0);
    font-size: 24px;
    font-family: ${props => (props.cnito ? 'Rodger' : 'LubalinGraphStd')};
    font-weight: bold;
    text-align: center;
    text-transform: uppercase;
    line-height: 22px;
    margin: 0;
`;

const AvatarButton = styled.button`
    width: 121px;
    height: 121px;
    border: none;
    transition: all 0.15s ease-in-out;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;

    :not(:last-of-type) {
        margin-right: 16px;
    }

    ${notWebMediaQuery} {
        margin-bottom: 16px;
        margin-right: 16px;
    }

    ${({inactive}) =>
        inactive &&
        css`
            opacity: 0.4;
        `}

    ${({active, cnito}) =>
        active &&
        !cnito &&
        css`
            &,
            &:hover,
            &:active,
            &:focus {
                box-shadow: 0px 2px 12px 0px rgba(0, 0, 0, 0.5) !important;
            }
        `}

    ${({background}) =>
        background &&
        css`
            background-image: url(${background});
        `}

    ${({cnito}) =>
        cnito &&
        css`
            transition: none;
            width: 140px;
            height: 140px;

            background-color: transparent;

            :not(:last-of-type) {
                margin-right: 32px;
            }

            ${notWebMediaQuery} {
                margin-bottom: 26px;
                margin-right: 32px;
            }

            ${mobileMediaQuery} {
                width: 126px;
                height: 126px;
            }

            &,
            &:hover,
            &:active,
            &:focus {
                box-shadow: none;
            }

            div {
                overflow: initial !important;
            }

            svg {
                transform: translateY(-42px) scale(2) !important;
            }
        `}

    svg {
        pointer-events: none;
    }

    &:focus {
        outline: none;
    }
`;

export default Onboarding;
